/**
 * @module Existing Customer Modal
 * @description Existing Customer Modal
 */

import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import MicroModal from "micromodal";
import PropTypes from "prop-types";
import Link from "next/link";
import { getCookie } from "@/js/utils/cookie";
import { stringify } from "@/js/utils/filters";
import { trackEvent } from "@/js/helpers/tracking";
import useMediaQuery from "@/hooks/useMediaQuery";

import styles from "./index.module.css";
/**
 * @function Component
 * @description
 */
const Component = ({ ...props }) => {
  const {
    heading = "Existing customer?",
    subheading = "Sign in to unlock exclusive deals and promotions, designed just for you!",
    mobileSubheading = "Sign in, or visit the MyFrontier® mobile app to unlock exclusive deals and promotions — designed just for you!",
    ctaSubCopy = "I'm looking to become a Frontier Customer.",
  } = props;
  const router = useRouter();
  const [modalVisible, setModalVisible] = useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");

  /**
   * @function handleClick
   * @description fire off tagular events
   */
  const handleClick = (event) => {
    const { target } = event;
    const payload = JSON.parse(target.getAttribute("data-payload"));
    const { text, actionOutcome } = payload;

    const data = {
      elementType: "BUTTON",
      position: "MODAL",
      text,
      actionOutcome,
    };

    trackEvent({ action: "elementClicked", data, event });
  };

  /**
   * @function handleClose
   * @description handle modal close
   */
  const handleClose = () => {
    const data = {
      elementType: "BUTTON",
      position: "MODAL",
      text: "Close",
      actionOutcome: "INTERNALLINK",
    };

    trackEvent({ action: "elementClicked", data, event });

    setModalVisible(false);
  };

  /**
   * @function useEffect
   * @description initialize the modal
   */
  useEffect(() => {
    if (modalVisible === false) {
      return;
    }

    MicroModal.init({
      openClass: "is-open",
      disableScroll: true,
      awaitOpenAnimation: false,
      awaitCloseAnimation: false,
      debugMode: false,
    });
    MicroModal.show("modal-existing-customer", {
      onClose: handleClose,
      disableFocus: true,
    });

    const data = {
      elementType: "MODAL",
      position: "EXISTING CUSTOMER MODAL",
    };

    trackEvent({ action: "elementViewed", data, event });
  }, [modalVisible]);

  /**
   * @function useEffect
   * @description Query param to trigger manually
   */
  useEffect(() => {
    if ("showModal" in router.query) {
      setModalVisible(true);
    }
  }, [router.query]);

  /**
   * @function useEffect
   * @description Kick off this modules functions
   */
  useEffect(() => {
    // If the user is an existing customer, show the modal
    if (getCookie("frontieramp")) {
      setModalVisible(true);
    }
  }, []);

  return (
    <>
      {modalVisible && (
        <section className={`component ${styles.component}`}>
          <div
            className="modal micromodal-slide"
            id="modal-existing-customer"
            aria-hidden="true"
          >
            <div className="modal__overlay" tabIndex="-1" data-micromodal-close>
              <div
                className={`modal__container ${styles.component__modalContainer}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-existing-customer-heading"
              >
                <button
                  className={`modal__close ${styles.component__close}`}
                  aria-label="Close modal"
                  data-micromodal-close
                ></button>
                <div className={styles.component__contentWrapper}>
                  <div className={`modal__header ${styles.component__header}`}>
                    <span
                      className={`icon icon-account ${styles.component__icon}`}
                    />
                    <p
                      className={`h4 ${styles.component__heading}`}
                      id="modal-existing-customer-heading"
                    >
                      {heading}
                    </p>
                    <p className={styles.component__subheading}>
                      {isMobile ? mobileSubheading : subheading}
                    </p>
                  </div>
                  {/* <!-- .modal__header --> */}

                  <div
                    className={`modal__content ${styles.component__body}`}
                    id="modal-existing-customer-content"
                  >
                    <div className={styles.component__blocks}>
                      <Link
                        href={"/pages/login"}
                        className={`btn btn--red btn-large ${styles.component__blockBtn}`}
                        onClick={handleClick}
                        data-payload={stringify({
                          text: "Sign in",
                          position: "Banner",
                          actionOutcome: "INTERALLINK",
                        })}
                      >
                        {"Sign In"}
                      </Link>
                    </div>
                    {/* <!-- .component__block --> */}
                    <p className={`h5 ${styles.component__ctaSubCopy}`}>
                      <Link key="link-1" href="/buy" onClick={handleClick}>
                        {ctaSubCopy}
                      </Link>
                    </p>
                  </div>
                  {/* <!-- .modal__content --> */}
                </div>
                {/* <!-- .component__contentWrapper --> */}
              </div>
              {/* <!-- .modal__container --> */}
            </div>
            {/* <!-- .modal__overlay --> */}
          </div>
          {/* <!-- .modal --> */}
        </section>
      )}
    </>
  );
};

Component.propTypes = {
  heading: PropTypes.string,
  subheading: PropTypes.string,
  mobileSubheading: PropTypes.string,
  ctaSubCopy: PropTypes.string,
};

export default Component;
